import * as gqlTypes from "@hydrologiq/gql-experience/graphql"

/**
 * Computes the current status of an activity based on its most recent event
 */
export function computeActivityStatus(activity: gqlTypes.Activity): "Pending" | "Started" | "Completed" {
  if (!activity.events || activity.events.length === 0) {
    return "Pending"
  }

  // Sort events by created_at to find the most recent one
  const sortedEvents = [...activity.events].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )

  const latestEvent = sortedEvents[0]

  // Convert event kind to display status
  switch (latestEvent.kind) {
    case gqlTypes.ActivityEventKind.Started:
      return "Started"
    case gqlTypes.ActivityEventKind.Completed:
      return "Completed"
    default:
      return "Pending"
  }
}

/**
 * Gets the next status and corresponding button text for an activity
 */
export function getNextAction(currentStatus: "Pending" | "Started" | "Completed"): {
  nextEventKind: gqlTypes.ActivityEventKind | null
  buttonText: string
  dialogTitle: string
} {
  switch (currentStatus) {
    case "Pending":
      return {
        nextEventKind: gqlTypes.ActivityEventKind.Started,
        buttonText: "Start",
        dialogTitle: "Start Activity",
      }
    case "Started":
      return {
        nextEventKind: gqlTypes.ActivityEventKind.Completed,
        buttonText: "Complete",
        dialogTitle: "Complete Activity",
      }
    case "Completed":
      return {
        nextEventKind: null,
        buttonText: "",
        dialogTitle: "",
      }
    default:
      return {
        nextEventKind: null,
        buttonText: "",
        dialogTitle: "",
      }
  }
}

/**
 * Gets the current status as ActivityEventKind for internal use
 */
export function computeActivityStatusAsEventKind(activity: gqlTypes.Activity): gqlTypes.ActivityEventKind {
  if (!activity.events || activity.events.length === 0) {
    return gqlTypes.ActivityEventKind.Started
  }

  // Sort events by created_at to find the most recent one
  const sortedEvents = [...activity.events].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )

  const latestEvent = sortedEvents[0]
  return latestEvent.kind
}

/**
 * Formats activity kind for display
 */
export function formatActivityKind(kind: gqlTypes.ActivityKind): string {
  switch (kind) {
    case gqlTypes.ActivityKind.Filling:
      return "Filling"
    case gqlTypes.ActivityKind.Movement:
      return "Movement"
    case gqlTypes.ActivityKind.Maintenance:
      return "Maintenance"
    case gqlTypes.ActivityKind.Inspection:
      return "Inspection"
    case gqlTypes.ActivityKind.Fulfilment:
      return "Fulfilment"
    case gqlTypes.ActivityKind.Hold:
      return "Hold"
    default:
      return kind
  }
}
